import React from "react";
import styled from "styled-components";
import { usePerformance } from "../../../context/PerformanceContext";
import PerformanceGrid from "./PerformanceGrid";

interface PerformanceSectionProps {
  disabled?: boolean;
  hideStrum?: boolean;
}

const GROOVE_COMMIT_INTERVAL_MS = 90;

const PerformanceSection: React.FC<PerformanceSectionProps> = ({
  disabled = false,
  hideStrum = false,
}) => {
  // Use the shared performance context
  const { strum, setStrum, groove, setGroove } = usePerformance();
  const strumRafRef = React.useRef<number | null>(null);
  const grooveTimeoutRef = React.useRef<number | null>(null);
  const pendingStrumRef = React.useRef<number | null>(null);
  const pendingGrooveRef = React.useRef<number | null>(null);
  const lastStrumRef = React.useRef<number | null>(null);
  const lastGrooveRef = React.useRef<number | null>(null);
  const isStrumDraggingRef = React.useRef<boolean>(false);
  const isGrooveDraggingRef = React.useRef<boolean>(false);
  const [strumDraft, setStrumDraft] = React.useState<number>(strum);
  const [grooveDraft, setGrooveDraft] = React.useState<number>(groove);

  React.useEffect(() => {
    lastStrumRef.current = strum;
    if (!isStrumDraggingRef.current) {
      setStrumDraft(strum);
    }
  }, [strum]);

  React.useEffect(() => {
    lastGrooveRef.current = groove;
    if (!isGrooveDraggingRef.current) {
      setGrooveDraft(groove);
    }
  }, [groove]);

  const flushStrum = React.useCallback(() => {
    if (strumRafRef.current !== null) {
      cancelAnimationFrame(strumRafRef.current);
      strumRafRef.current = null;
    }

    if (pendingStrumRef.current === null) {
      return;
    }

    const nextValue = pendingStrumRef.current;
    pendingStrumRef.current = null;
    if (lastStrumRef.current === nextValue) {
      return;
    }

    lastStrumRef.current = nextValue;
    setStrum(nextValue);
  }, [setStrum]);

  const flushGroove = React.useCallback(() => {
    if (grooveTimeoutRef.current !== null) {
      window.clearTimeout(grooveTimeoutRef.current);
      grooveTimeoutRef.current = null;
    }

    if (pendingGrooveRef.current === null) {
      return;
    }

    const nextValue = pendingGrooveRef.current;
    pendingGrooveRef.current = null;
    if (lastGrooveRef.current === nextValue) {
      return;
    }

    lastGrooveRef.current = nextValue;
    setGroove(nextValue);
  }, [setGroove]);

  const queueStrum = React.useCallback(
    (nextValue: number) => {
      pendingStrumRef.current = nextValue;
      if (strumRafRef.current !== null) {
        return;
      }

      strumRafRef.current = window.requestAnimationFrame(() => {
        strumRafRef.current = null;
        flushStrum();
      });
    },
    [flushStrum]
  );

  const queueGroove = React.useCallback(
    (nextValue: number) => {
      pendingGrooveRef.current = nextValue;
      if (grooveTimeoutRef.current !== null) {
        return;
      }

      grooveTimeoutRef.current = window.setTimeout(() => {
        grooveTimeoutRef.current = null;
        flushGroove();
      }, GROOVE_COMMIT_INTERVAL_MS);
    },
    [flushGroove]
  );

  const formatPercentage = (value: number) => `${Math.round(value * 100)}%`;
  const formatSignedPercentage = (value: number) => {
    const rounded = Math.round(value * 100);
    if (rounded > 0) {
      return `+${rounded}%`;
    }
    if (rounded < 0) {
      return `${rounded}%`;
    }
    return "0%";
  };

  const handleStrumChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseFloat(e.target.value);
    setStrumDraft(newValue);
    queueStrum(newValue);
  };

  const handleGrooveChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseFloat(e.target.value);
    setGrooveDraft(newValue);
    queueGroove(newValue);
  };

  const handleStrumPointerDown = () => {
    isStrumDraggingRef.current = true;
  };

  const handleStrumPointerUp = () => {
    isStrumDraggingRef.current = false;
    flushStrum();
  };

  const handleGroovePointerDown = () => {
    isGrooveDraggingRef.current = true;
  };

  const handleGroovePointerUp = () => {
    isGrooveDraggingRef.current = false;
    flushGroove();
  };

  const handleStrumReset = React.useCallback(() => {
    pendingStrumRef.current = null;
    setStrumDraft(0);
    flushStrum();
    if (lastStrumRef.current === 0) {
      return;
    }

    lastStrumRef.current = 0;
    setStrum(0);
  }, [flushStrum, setStrum]);

  const handleGrooveReset = React.useCallback(() => {
    pendingGrooveRef.current = null;
    setGrooveDraft(0);
    flushGroove();
    if (lastGrooveRef.current === 0) {
      return;
    }

    lastGrooveRef.current = 0;
    setGroove(0);
  }, [flushGroove, setGroove]);

  React.useEffect(
    () => () => {
      if (strumRafRef.current !== null) {
        cancelAnimationFrame(strumRafRef.current);
      }
      if (grooveTimeoutRef.current !== null) {
        window.clearTimeout(grooveTimeoutRef.current);
      }
    },
    []
  );

  return (
    <PerformanceSectionWrapper>
      {!hideStrum && (
        <StrumAndRegisterWrapper>
          <GrooveSection id="groove-section">
            <GrooveTitle id="groove-title">Groove</GrooveTitle>
            <GrooveSlider
              id="groove-slider"
              type="range"
              min="-1"
              max="1"
              step="0.01"
              value={grooveDraft}
              onChange={handleGrooveChange}
              onDoubleClick={handleGrooveReset}
              onPointerDown={handleGroovePointerDown}
              onPointerUp={handleGroovePointerUp}
              onPointerCancel={handleGroovePointerUp}
              disabled={disabled}
            />
            <GrooveValueRow id="groove-value-row">
              <GrooveLabel id="groove-label-pull">Pull</GrooveLabel>
              <GrooveValue id="groove-value">
                {formatSignedPercentage(grooveDraft)}
              </GrooveValue>
              <GrooveLabel id="groove-label-push">Push</GrooveLabel>
            </GrooveValueRow>
          </GrooveSection>
          <StrumSection id="strum-section">
            <StrumTitle id="strum-title">Strum</StrumTitle>
            <StrumSlider
              id="strum-slider"
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={strumDraft}
              onChange={handleStrumChange}
              onDoubleClick={handleStrumReset}
              onPointerDown={handleStrumPointerDown}
              onPointerUp={handleStrumPointerUp}
              onPointerCancel={handleStrumPointerUp}
              disabled={disabled}
            />
            <StrumValue id="strum-value">
              {formatPercentage(strumDraft)}
            </StrumValue>
          </StrumSection>
        </StrumAndRegisterWrapper>
      )}
      <PerformanceGrid />
    </PerformanceSectionWrapper>
  );
};

export default PerformanceSection;

const PerformanceSectionWrapper = styled.div.attrs({
  id: "performance-section-wrapper",
})`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-self: stretch;
  width: 160px;
  max-width: 160px;
  flex-shrink: 0;
`;

const StrumAndRegisterWrapper = styled.div.attrs({
  id: "strum-and-register-wrapper",
})`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 12px;
  padding-top: 4px;
`;

const StrumSection = styled.div.attrs({
  id: "strum-section",
})`
  padding: 16px 12px 12px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  position: relative;
  border: 2px solid #ccc;
  border-radius: 8px;
  margin-top: 2px;

  @media (max-width: 768px) {
    width: 100%;
    margin-top: 12px;
  }
`;

const StrumTitle = styled.h3.attrs({
  id: "strum-title",
})`
  margin: 0;
  font-size: 14px;
  font-weight: bold;
  color: #333;
  text-align: center;
  letter-spacing: 0.5px;
  position: absolute;
  top: -8px;
  left: 50%;
  transform: translateX(-50%);
  background: #e2e1dd;
  padding: 0 12px;
  border-radius: 4px;
`;

const StrumSlider = styled.input.attrs({
  id: "strum-slider",
})`
  width: 100%;
  height: 4px;
  background: #000;
  border-radius: 2px;
  outline: none;
  cursor: pointer;
  -webkit-appearance: none;
  appearance: none;
  position: relative;
  margin-top: 8px;

  &::-webkit-slider-runnable-track {
    height: 4px;
    background: #000;
    border-radius: 2px;
  }

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    background: #e2e8f0;
    border: 1px solid #94a3b8;
    border-radius: 50%;
    cursor: pointer;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    transition: all 0.2s ease;
    position: relative;
    margin-top: -6px;
  }

  &::-webkit-slider-thumb:hover {
    transform: scale(1.1);
  }

  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    background: #e2e8f0;
    border-radius: 50%;
    cursor: pointer;
    border: none;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    transition: all 0.2s ease;
  }

  &::-moz-range-thumb:hover {
    transform: scale(1.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StrumValue = styled.div.attrs({
  id: "strum-value",
})`
  font-size: 12px;
  font-weight: 500;
  color: #4a5568;
  text-align: center;
`;

const GrooveSection = styled(StrumSection).attrs({
  id: "groove-section",
})``;

const GrooveTitle = styled(StrumTitle).attrs({
  id: "groove-title",
})``;

const GrooveSlider = styled(StrumSlider).attrs({
  id: "groove-slider",
})``;

const GrooveValue = styled(StrumValue).attrs({
  id: "groove-value",
})``;

const GrooveValueRow = styled.div.attrs({
  id: "groove-value-row",
})`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 8px;
`;

const GrooveLabel = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: #4a5568;
`;
